import { PaginationProps } from '@customTypes/shared/Pagination';
import { Column, TableFetchDataType } from '@customTypes/table';
import TableWithFetchPagination from './TableWithFetchPagination';
import TableWithFetchSearch from './TableWithFetchSearch';
import useTableWithFetch from './useTableWithFetch';

const TableWithFetch = <TRow extends { key?: number; id?: number }>({
    columns = [],
    rows = [],
    isLoading = false,
    pageSizes = [10, 25, 100],
    noDataMessage = 'There is no data to display',
    fetchData = () => {},
    currentPage,
    pageSize,
    totalPages,
    totalCount,
    searchType,
}: TableProps<TRow>): JSX.Element => {
    const {
        sortedRows,
        handleSortClick,
        sortColumn,
        sortAsc,
        page,
        setPage,
        pageSizeOpts,
        curPageSize,
        setPageSize,
        paginationDescription,
        searchTerm,
        setSearchTerm,
        canSearch,
    } = useTableWithFetch<TRow>({
        columns,
        rows,
        pageSizes,
        fetchData,
        currentPage,
        pageSize,
        totalPages,
        totalCount,
        searchType,
    });

    const isEmpty = !rows.length;

    return (
        <>
            <div className="table-top-filter flex-row-reverse justify-between align-center">
                {!isEmpty && (
                    <TableWithFetchPagination
                        page={page}
                        setPage={setPage}
                        maxPage={totalPages}
                        pageSizeOpts={pageSizeOpts}
                        pageSize={curPageSize}
                        setPageSize={setPageSize}
                        description={paginationDescription}
                    />
                )}

                {canSearch && <TableWithFetchSearch value={searchTerm} onChange={setSearchTerm} />}
            </div>

            <table>
                <thead>
                    <tr>{columns.map(renderHeading)}</tr>
                </thead>
                <tbody>
                    {isEmpty && !isLoading && (
                        <tr className="no-data-row">
                            <td colSpan={columns.length}>{noDataMessage}</td>
                        </tr>
                    )}
                    {sortedRows.map((row, index) => (
                        <tr key={row.key || row.id || index}>
                            {columns.map(col => (
                                <td key={col.key}>{col.getValue(row)}</td>
                            ))}
                        </tr>
                    ))}
                    {isLoading && (
                        <tr className="table-loading">
                            <td className="overlay"></td>
                            <td className="content">
                                <i className="fal fa-spinner fa-spin"></i>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {!isEmpty && (
                <TableWithFetchPagination
                    page={page}
                    setPage={setPage}
                    maxPage={totalPages}
                    pageSizeOpts={pageSizeOpts}
                    pageSize={curPageSize}
                    setPageSize={setPageSize}
                    description={paginationDescription}
                />
            )}
        </>
    );

    function renderHeading(col: Column<TRow>) {
        const { key, heading, getSort } = col;
        const canSort = getSort !== undefined;
        const isSorting = sortColumn?.key === key;

        return (
            <th
                key={key}
                className={canSort ? 'sortable' : ''}
                onClick={e => handleSortClick(e, col)}
            >
                {isSorting && sortAsc && <i className="fad fa-sort-up" />}
                {isSorting && !sortAsc && <i className="fad fa-sort-down" />}
                {heading}
            </th>
        );
    }
};

interface TableProps<T> extends PaginationProps {
    columns: Column<T>[];
    rows: T[];
    isLoading?: boolean;
    pageSizes?: number[];
    noDataMessage?: string;
    fetchData: TableFetchDataType;
    searchType: string;
}

export default TableWithFetch;
